import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  Dispatch,
  SetStateAction,
  useCallback,
} from "react";
import { lisapropApi } from "configurations/AxiosConfig";
import { useNavigate } from "react-router-dom";
import { userType, visitorType } from "types/allTypes";

interface LisapropContextProps {
  user: userType;
  setUser: Dispatch<SetStateAction<userType>>;
  lisapropVisitor: visitorType;
  setLisapropVisitor: Dispatch<SetStateAction<visitorType>>;
  handleLogin: (email: string, password: string) => Promise<void>;
  storedToken: string | null;
  setStoredToken: Dispatch<SetStateAction<string | null>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  errorMessage: string | null;
  setErrorMessage: Dispatch<SetStateAction<string | null>>;
  fetchUserData: (accessToken: string) => Promise<void>;
  trackAProject: boolean;
  setTrackAProject: Dispatch<SetStateAction<boolean>>;
  setTrackPropertyId: Dispatch<SetStateAction<string>>;
  trackPropertyId: string;
  setListPropertyId: Dispatch<SetStateAction<string>>;
  addMemberFee: number;
  setAddMemberFee: Dispatch<SetStateAction<number>>;
  listPropertyId: string;
  buildNdGoWithLand: boolean;
  setBuildNdGoWithLand: Dispatch<SetStateAction<boolean>>;
  buildNdGoNoLand: boolean;
  setBuildNdGoNoLand: Dispatch<SetStateAction<boolean>>;
  colabWithDevWithLand: boolean;
  setColabWithDevWithLand: Dispatch<SetStateAction<boolean>>;
  colabWithDevNoLand: boolean;
  setColabWithDevNoLand: Dispatch<SetStateAction<boolean>>;
  viewProjectDetails: boolean;
  setViewProjectDetails: Dispatch<SetStateAction<boolean>>;
  projectTab: string;
  setProjectTab: Dispatch<SetStateAction<"listProperty" | "trackProject">>;
  devProjectTab: string;
  setDevProjectTab: Dispatch<
    SetStateAction<"sentProposals" | "monitoredProjects">
  >;
  currentProjectId: string;
  setCurrentProjectId: Dispatch<SetStateAction<string>>;
  itemDeleted: boolean;
  setItemDeleted: Dispatch<SetStateAction<boolean>>;
  siteImages: any;
  setSiteImages: any;
  siteImagesFile: any;
  setSiteImagesFile: any;
  visitorSignedIn: boolean;
  setVisitorSignedIn: Dispatch<SetStateAction<boolean>>;
  newMemberAddedToStorage: boolean;
  setNewMemberAddedToStorage: Dispatch<SetStateAction<boolean>>;
  userLoading: boolean;
  setUserLoading: Dispatch<SetStateAction<boolean>>;
}
const AuthContext = createContext<LisapropContextProps | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState({} as userType);
  const [lisapropVisitor, setLisapropVisitor] = useState({} as visitorType);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>("");
  const [storedToken, setStoredToken] = useState<string | null>("");
  const [trackAProject, setTrackAProject] = useState<boolean>(false);
  const [trackPropertyId, setTrackPropertyId] = useState("");
  const [listPropertyId, setListPropertyId] = useState("");
  const [buildNdGoWithLand, setBuildNdGoWithLand] = useState(false);
  const [buildNdGoNoLand, setBuildNdGoNoLand] = useState(false);
  const [colabWithDevWithLand, setColabWithDevWithLand] = useState(false);
  const [colabWithDevNoLand, setColabWithDevNoLand] = useState(false);
  const [viewProjectDetails, setViewProjectDetails] = useState(false);
  const [projectTab, setProjectTab] = useState<"listProperty" | "trackProject">(
    "listProperty"
  );
  const [devProjectTab, setDevProjectTab] = useState<
    "sentProposals" | "monitoredProjects"
  >("sentProposals");
  const [currentProjectId, setCurrentProjectId] = useState("");
  const [addMemberFee, setAddMemberFee] = useState<number>(0);
  const [itemDeleted, setItemDeleted] = useState(false);
  const [siteImages, setSiteImages] = useState<any[]>([]);
  const [siteImagesFile, setSiteImagesFile] = useState<any[]>([]);
  const [visitorSignedIn, setVisitorSignedIn] = useState(false);
  const [newMemberAddedToStorage, setNewMemberAddedToStorage] = useState(false);
  const [userLoading, setUserLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  // const logout = () => {
  //   localStorage.removeItem("accessToken");
  //   setUser({} as UserData);
  //   setIsLoading(false)
  //   setUserDetails({} as UserDetails);
  //   setStoredToken(null);
  //   setIsWalletConfirmed(false);
  // };
  const fetchUserData = useCallback(
    async (accessToken: string) => {
      setUserLoading(true);
      try {
        const response = await lisapropApi.get("/user", {
          headers: {
            Accept: "text/plain",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });
        setUser(response?.data);
      } catch (error: any) {
        console.error("Unexpected error:", error);
        if (error?.response?.status === 401) {
          localStorage.clear();
          navigate("/login");
        }
      } finally {
        setUserLoading(false);
      }
    },
    [navigate]
  );

  const handleLogin = async (email: string, password: string) => {
    setErrorMessage("");
    setIsLoading(true);
    try {
      const response = await lisapropApi.post(
        `/auth/signin`,
        {
          email: email,
          password: password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 200) {
        setIsLoading(false);
        navigate("/dashboard");
        // console.log("succesfull login response", response);
        const accessToken = response.data.accessToken;
        localStorage.setItem("accessToken", accessToken);
        setStoredToken(accessToken);
        fetchUserData(accessToken);
      }
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message);
      setIsLoading(false);
      console.log(error);
    }
  };

  useEffect(() => {
    const theStoredToken = localStorage.getItem("accessToken");
    if (theStoredToken) {
      fetchUserData(theStoredToken);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        lisapropVisitor,
        setLisapropVisitor,
        setStoredToken,
        handleLogin,
        storedToken,
        setIsLoading,
        isLoading,
        errorMessage,
        setErrorMessage,
        fetchUserData,
        trackAProject,
        setTrackAProject,
        setTrackPropertyId,
        trackPropertyId,
        setListPropertyId,
        listPropertyId,
        buildNdGoNoLand,
        setBuildNdGoNoLand,
        buildNdGoWithLand,
        setBuildNdGoWithLand,
        colabWithDevWithLand,
        setColabWithDevWithLand,
        colabWithDevNoLand,
        setColabWithDevNoLand,
        setViewProjectDetails,
        viewProjectDetails,
        setProjectTab,
        projectTab,
        setCurrentProjectId,
        currentProjectId,
        devProjectTab,
        setDevProjectTab,
        itemDeleted,
        setItemDeleted,
        siteImages,
        setSiteImages,
        siteImagesFile,
        setSiteImagesFile,
        addMemberFee,
        setAddMemberFee,
        visitorSignedIn,
        setVisitorSignedIn,
        setNewMemberAddedToStorage,
        newMemberAddedToStorage,
        userLoading,
        setUserLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
